import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  faCheck,
  faHourglassStart,
  faPen,
  faPlus,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import {
  DecisivePlayerPickerMode,
  DecisivePlayerPickerTaskAppearanceStatus,
  GameStatus,
} from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Gift } from '@sorare/core/src/atoms/icons/Gift';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BrandXXS, LabelXS } from '@sorare/core/src/atoms/typography';
import { LiveDot } from '@sorare/core/src/components/live/LiveDot';
import { Score } from '@sorare/core/src/components/scoring/Score';
import { IMAGES, StatIcon } from '@sorare/core/src/components/stats/StatIcon';
import { isType } from '@sorare/core/src/lib/gql';
import type { Stats } from '@sorare/core/src/lib/scoring';
import { statLabels } from '@sorare/core/src/lib/scoring';
import type { Color } from '@sorare/core/src/style/types';

import { BaseSlot } from '../BaseSlot';
import type {
  DesicivePickerSlot_card,
  DesicivePickerSlot_taskAppearance,
} from './__generated__/index.graphql';

const Action = styled(Horizontal).attrs({ gap: 0, center: true })`
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(30%, -30%);
  font-size: var(--fs-10);
  border-radius: 50%;
  background-color: var(--action-bg-color, var(--c-white));
  color: var(--action-color, var(--c-black));
  padding: 2px;

  button:disabled & {
    display: none;
  }
`;
const GiftCircle = styled(Action)`
  transform: translate(25%, -25%);
  padding: var(--half-unit);
`;

const StyledLabelXS = styled(LabelXS)`
  padding: 2px var(--half-unit);
  display: flex;
  align-items: center;
  gap: var(--half-unit);
`;

export const DesicivePickerSlot = ({
  taskAppearance,
  card,
  isSelected,
  showLockedOverlay,
  index,
  showAction,
  mode,
}: {
  taskAppearance?: DesicivePickerSlot_taskAppearance;
  card?: DesicivePickerSlot_card;
  isSelected?: boolean;
  showLockedOverlay?: boolean;
  index: number;
  showAction?: boolean;
  mode: DecisivePlayerPickerMode;
}) => {
  const cardToDisplay = taskAppearance?.card || card;

  if (taskAppearance) {
    const { playerGameScore, locked } = taskAppearance;

    const gameStatus = playerGameScore?.anyGame?.statusTyped;

    const hasSucceed =
      isType(taskAppearance, 'DecisivePlayerPickerTaskAppearance') &&
      taskAppearance.status ===
        DecisivePlayerPickerTaskAppearanceStatus.SUCCESS;

    const positiveDecisiveStats = isType(playerGameScore, 'PlayerGameScore')
      ? playerGameScore?.positiveDecisiveStats
      : undefined;

    const hasFailed =
      isType(taskAppearance, 'DecisivePlayerPickerTaskAppearance') &&
      taskAppearance.status ===
        DecisivePlayerPickerTaskAppearanceStatus.FAILURE;

    if (
      mode === DecisivePlayerPickerMode.SCORE &&
      taskAppearance.playerGameScore
    ) {
      if (hasFailed || hasSucceed || gameStatus === GameStatus.playing) {
        let color: Color = 'var(--c-nd-150)';
        if (hasFailed) {
          color = 'var(--c-rewards-orange)';
        } else if (hasSucceed) {
          color = 'var(--c-onBase-success)';
        }
        return (
          <BaseSlot
            label={
              <Horizontal gap={0}>
                {gameStatus === GameStatus.playing && <LiveDot animate />}
                <Score
                  size={3}
                  hideReviewing
                  score={taskAppearance.playerGameScore.score}
                  scoreStatus={taskAppearance.playerGameScore.scoreStatus}
                  shape="PILL"
                  scoreType="PLAYER_SCORE"
                />
              </Horizontal>
            }
            showLockedOverlay={showLockedOverlay}
            color={color}
            card={cardToDisplay}
          />
        );
      }
    } else if (mode === DecisivePlayerPickerMode.DECISIVE) {
      if (hasSucceed) {
        const statKey =
          mode === DecisivePlayerPickerMode.DECISIVE &&
          positiveDecisiveStats?.find(stat => stat.statValue > 0)?.stat;

        return (
          <BaseSlot
            icon={
              <LabelXS color="var(--c-black)">
                {statKey && statKey in statLabels && statKey in IMAGES ? (
                  <StatIcon statKey={statKey as Stats} />
                ) : (
                  <FontAwesomeIcon icon={faCheck} />
                )}
              </LabelXS>
            }
            showLockedOverlay={showLockedOverlay}
            color="var(--c-onBase-success)"
            card={cardToDisplay}
          >
            {!!showAction && (
              <GiftCircle>
                <Gift size={1.5} />
              </GiftCircle>
            )}
          </BaseSlot>
        );
      }

      if (hasFailed) {
        return (
          <BaseSlot
            icon={
              <LabelXS color="var(--c-black)">
                <FontAwesomeIcon icon={faXmark} />
              </LabelXS>
            }
            showLockedOverlay={showLockedOverlay}
            color="var(--c-rewards-orange)"
            card={cardToDisplay}
          />
        );
      }

      if (gameStatus === GameStatus.playing) {
        return (
          <BaseSlot
            icon={
              <StyledLabelXS color="var(--c-white)">
                <LiveDot animate />
                <FontAwesomeIcon icon={faHourglassStart} />
              </StyledLabelXS>
            }
            showLockedOverlay={showLockedOverlay}
            color="var(--c-nd-150)"
            card={cardToDisplay}
          />
        );
      }
    }

    return (
      <BaseSlot
        icon={
          <StyledLabelXS color="var(--c-white)">
            <FontAwesomeIcon icon={faHourglassStart} />
          </StyledLabelXS>
        }
        showLockedOverlay={showLockedOverlay}
        color={isSelected ? 'var(--c-blue-400)' : 'var(--c-nd-150)'}
        card={cardToDisplay}
      >
        {!locked && !!showAction && (
          <Action
            style={
              {
                '--action-bg-color': 'var(--c-black)',
                '--action-color': 'var(--c-white)',
              } as CSSProperties
            }
          >
            <FontAwesomeIcon icon={faPen} />
          </Action>
        )}
      </BaseSlot>
    );
  }

  return (
    <BaseSlot
      icon={<BrandXXS>{index + 1}</BrandXXS>}
      showLockedOverlay={showLockedOverlay}
      color={isSelected ? 'var(--c-blue-400)' : 'var(--c-nd-150)'}
      card={cardToDisplay}
    >
      {!!showAction && (
        <Action>
          <FontAwesomeIcon icon={faPlus} />
        </Action>
      )}
    </BaseSlot>
  );
};

DesicivePickerSlot.fragments = {
  taskAppearance: gql`
    fragment DesicivePickerSlot_taskAppearance on TaskAppearanceInterface {
      id
      index
      card {
        slug
        ...CardAvatar_anyCard
        ...BaseSlot_anyCardInterface
      }
      playerGameScore {
        id
        score
        scoreStatus
        anyGame {
          id
          statusTyped
        }
        ... on PlayerGameScore {
          scoreStatus
          id
          positiveDecisiveStats {
            stat
            statValue
          }
        }
      }
      locked
      ... on DecisivePlayerPickerTaskAppearance {
        id
        status
      }
    }
    ${BaseSlot.fragments.anyCardInterface}
  ` as TypedDocumentNode<DesicivePickerSlot_taskAppearance>,
  card: gql`
    fragment DesicivePickerSlot_card on AnyCardInterface {
      slug
      ...BaseSlot_anyCardInterface
    }
    ${BaseSlot.fragments.anyCardInterface}
  ` as TypedDocumentNode<DesicivePickerSlot_card>,
};
